// CeKKeU 2.0 — Client-side PDF export of the monthly insight + trend chart.
// Generated entirely in the browser (jsPDF). NOTHING is sent to the server.
import { jsPDF } from "jspdf";
import { monthlySummary, monthlySeries } from "@/lib/insights";
import { formatCompact } from "@/lib/currency";

const EMERALD = [5, 150, 105];
const ROSE = [225, 29, 72];
const ZINC = [113, 113, 122];
const DARK = [24, 24, 27];
const STATUS_RGB = { sehat: EMERALD, waspada: [217, 119, 6], perhatian: ROSE };

export function exportInsightPdf(finance, opts = {}) {
  const months = opts.months || 6;
  const offset = opts.offset || 0;
  const s = monthlySummary(finance, offset);
  const trend = monthlySeries(finance?.transactions || [], months);

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const M = 48;
  let y = 56;

  const ensure = (need) => {
    if (y + need > H - 60) {
      doc.addPage();
      y = 56;
    }
  };
  const line = () => {
    doc.setDrawColor(228, 228, 231);
    doc.line(M, y, W - M, y);
    y += 14;
  };
  const label = (txt) => {
    ensure(24);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...ZINC);
    doc.text(txt.toUpperCase(), M, y);
    y += 16;
  };
  const row = (k, v, rgb = DARK) => {
    ensure(18);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(...ZINC);
    doc.text(k, M, y);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...rgb);
    doc.text(String(v), W - M, y, { align: "right" });
    y += 18;
  };
  const bullet = (txt, rgb = DARK) => {
    const lines = doc.splitTextToSize(txt, W - 2 * M - 14);
    ensure(lines.length * 14 + 4);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10.5);
    doc.setTextColor(...rgb);
    doc.text("•", M, y);
    doc.text(lines, M + 14, y);
    y += lines.length * 14 + 4;
  };

  // ---- Header ----
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...DARK);
  doc.text("CeKKeU", M, y);
  doc.setFontSize(11);
  doc.setTextColor(...ZINC);
  doc.text("Ringkasan Keuangan", W - M, y, { align: "right" });
  y += 22;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(...DARK);
  doc.text(s.monthLabel, M, y);
  if (s.hasData) {
    const st = STATUS_RGB[s.status.level] || ZINC;
    doc.setTextColor(...st);
    doc.text(s.status.label, W - M, y, { align: "right" });
  }
  y += 16;
  line();

  if (!s.hasData) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(...ZINC);
    doc.text("Belum ada transaksi pada bulan ini.", M, y);
    doc.save(`CeKKeU-${s.monthLabel}.pdf`);
    return;
  }

  // ---- Ringkasan bulan ----
  label("Ringkasan Bulan Ini");
  row("Total pemasukan", formatCompact(s.income), EMERALD);
  row("Total pengeluaran", formatCompact(s.expense), ROSE);
  row(s.net >= 0 ? "Surplus" : "Defisit", (s.net >= 0 ? "" : "-") + formatCompact(Math.abs(s.net)), s.net >= 0 ? DARK : ROSE);
  row("Saving rate", s.savingRate != null ? `${Math.round(s.savingRate * 100)}%` : "–");
  row("Rasio cicilan", s.debtRatio != null ? `${Math.round(s.debtRatio * 100)}%` : "–");
  row("Dana darurat", `${s.emergency.months >= 6 ? "6+" : s.emergency.months.toFixed(1).replace(".", ",")} bulan`);
  row("Skor kesehatan rata-rata", s.avgHealth ? String(s.avgHealth.value) : "–");
  if (s.prev.available && s.prev.changePct != null)
    row("vs bulan lalu", `Pengeluaran ${s.prev.changePct > 0 ? "naik" : "turun"} ${Math.abs(Math.round(s.prev.changePct * 100))}%`);
  y += 6;

  // ---- Trend chart (drawn bars) ----
  if (trend.series.length) {
    label(`Tren ${trend.monthsAvailable} Bulan`);
    ensure(170);
    const chartX = M;
    const chartW = W - 2 * M;
    const chartH = 120;
    const baseY = y + chartH;
    const maxVal = Math.max(1, ...trend.series.map((m) => Math.max(m.income, m.expense)));
    const n = trend.series.length;
    const slotW = chartW / n;
    const barW = Math.min(18, slotW * 0.32);

    // axis baseline
    doc.setDrawColor(228, 228, 231);
    doc.line(chartX, baseY, chartX + chartW, baseY);

    trend.series.forEach((m, i) => {
      const cx = chartX + slotW * i + slotW / 2;
      const ih = (m.income / maxVal) * chartH;
      const eh = (m.expense / maxVal) * chartH;
      doc.setFillColor(...EMERALD);
      doc.rect(cx - barW - 2, baseY - ih, barW, ih, "F");
      doc.setFillColor(...ROSE);
      doc.rect(cx + 2, baseY - eh, barW, eh, "F");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...ZINC);
      doc.text(m.label, cx, baseY + 12, { align: "center" });
    });
    y = baseY + 24;
    // legend
    doc.setFontSize(8);
    doc.setFillColor(...EMERALD); doc.rect(chartX, y - 7, 8, 8, "F");
    doc.setTextColor(...ZINC); doc.text("Masuk", chartX + 12, y);
    doc.setFillColor(...ROSE); doc.rect(chartX + 60, y - 7, 8, 8, "F");
    doc.text("Keluar", chartX + 72, y);
    y += 18;

    // compact table
    trend.series.forEach((m) => {
      ensure(16);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9.5);
      doc.setTextColor(...ZINC);
      doc.text(m.label, M, y);
      doc.setTextColor(...EMERALD); doc.text(formatCompact(m.income), M + 90, y);
      doc.setTextColor(...ROSE); doc.text(formatCompact(m.expense), M + 190, y);
      doc.setTextColor(...(m.net >= 0 ? DARK : ROSE));
      doc.text((m.net >= 0 ? "" : "-") + formatCompact(Math.abs(m.net)), M + 290, y);
      y += 15;
    });
    y += 6;
  }

  // ---- Top categories ----
  if (s.topCategories.length) {
    label(`${s.topCategories.length} Pengeluaran Terbesar`);
    s.topCategories.forEach((c) => row(c.name, `${formatCompact(c.amount)} · ${Math.round(c.pct * 100)}%`));
    y += 6;
  }

  // ---- Target vs realisasi ----
  if (s.budgetRows.length) {
    label("Target vs Realisasi");
    s.budgetRows.forEach((b) =>
      row(b.category, `${formatCompact(b.used)} / ${formatCompact(b.target)}${b.over > 0 ? ` · lewat ${formatCompact(b.over)}` : ""}`, b.over > 0 ? ROSE : DARK)
    );
    y += 6;
  }

  // ---- Insights ----
  if (s.insights.length) {
    label("Insight Utama");
    s.insights.forEach((t) => bullet(t));
    y += 4;
  }

  // ---- Recommendations ----
  label("Rekomendasi Bulan Depan");
  s.recommendations.forEach((t) => bullet(t, EMERALD));

  // ---- Footer ----
  ensure(40);
  y += 8;
  line();
  doc.setFont("helvetica", "italic");
  doc.setFontSize(8.5);
  doc.setTextColor(...ZINC);
  const today = new Date().toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
  doc.text(`Dibuat di perangkat Anda pada ${today}. Data keuangan tidak dikirim ke server CeKKeU.`, M, y);

  doc.save(`CeKKeU-${s.monthLabel}.pdf`);
}
